// src/lib/collect.ts — colectare din teren (F9-T2/T3): chei de sistem + poze pe feature.
// Helpers PURI, partajați server↔client (ca lib/rlu.ts) — o SINGURĂ listă de chei, fără drift
// între formulare (client), PATCH-ul de feature (server) și filtrul căilor publice (public.ts).

/**
 * Amprentele capturii din teren (REQ-MOB-014), scrise SERVER-SIDE la creare cu `collect`.
 * `collected_department` (4.11): departamentul din membership-ul colectorului în org-ul hărții
 * (`actor_department`, auth.ts) — absent când colectorul n-are departament.
 */
export const COLLECT_KEYS = [
	"collected_at",
	"collected_by",
	"gps_accuracy",
	"collected_department",
] as const

/** Cheia EAV cu lista pozelor de teren ale unui feature (F9-T3). */
export const PHOTOS_KEY = "photos"

/**
 * Toate cheile de SISTEM de pe feature: respinse la PATCH (nemodificabile din formular/API),
 * ascunse din formularele de editare și EXCLUSE necondiționat de pe căile publice
 * (`collected_by` e un email — dată personală; restul sunt metadate operaționale).
 */
export const SYSTEM_FEATURE_KEYS: readonly string[] = [...COLLECT_KEYS, PHOTOS_KEY]

/** Scoate cheile de sistem dintr-un dicționar de proprietăți (redactare pe căile ne-staff). */
export function strip_system_keys(props: Record<string, unknown>): Record<string, unknown> {
	return Object.fromEntries(Object.entries(props).filter(([k]) => !SYSTEM_FEATURE_KEYS.includes(k)))
}

export const MAX_PHOTOS_PER_FEATURE = 10
/** Plafonul serverului per poză; clientul comprimă oricum la ~300KB înainte de upload. */
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024

/** O poză de teren: `key` = cheia de storage `features/<feature_id>/<uuid>`. */
export interface FeaturePhoto {
	key: string
	mime: string
	size: number
	at: string // ISO — momentul upload-ului
}

// Obiect nil lângă tip (regula din CLAUDE.md) — RESULT.data e mereu valid.
export const NIL_FEATURE_PHOTO: FeaturePhoto = Object.freeze({ key: "", mime: "", size: 0, at: "" })

/**
 * Confinare fail-closed: cheia unei poze trebuie să stea SUB feature-ul ei. O cheie forjată în
 * EAV (import vechi, scriere directă în DB) nu poate atinge alte obiecte din storage
 * (`documents/…`, `requests/…`) prin rutele de servire/ștergere ale pozelor.
 */
export function photo_key_ok(p: FeaturePhoto, feature_id: string): boolean {
	return p.key.startsWith(`features/${feature_id}/`)
}

/** uuid-ul unei poze = segmentul final al cheii de storage (identitatea din URL-ul API). */
export function photo_uuid(p: FeaturePhoto): string {
	const idx = p.key.lastIndexOf("/")
	return idx >= 0 ? p.key.slice(idx + 1) : p.key
}

/**
 * Parsare DEFENSIVĂ a valorii EAV `photos` — EAV-ul poate conține orice (import, API vechi).
 * Acceptă și STRING JSON: MapLibre serializează proprietățile ne-primitive ale features-urilor
 * interogate de pe hartă (queryRenderedFeatures) — popup-ul primește array-ul ca text.
 * Intrările fără formă validă se ignoră tăcut; niciodată throw.
 */
export function parse_photos(value: unknown): FeaturePhoto[] {
	if (typeof value === "string") {
		try {
			value = JSON.parse(value)
		} catch {
			return []
		}
	}
	if (!Array.isArray(value)) {
		return []
	}
	const out: FeaturePhoto[] = []
	for (const v of value) {
		if (v && typeof v === "object" && !Array.isArray(v)) {
			const o = v as Record<string, unknown>
			if (typeof o.key === "string" && o.key.length > 0) {
				out.push({
					key: o.key,
					mime: typeof o.mime === "string" ? o.mime : "image/jpeg",
					size: typeof o.size === "number" ? o.size : 0,
					at: typeof o.at === "string" ? o.at : "",
				})
			}
		}
	}
	return out
}

/**
 * Tipul unei POZE din magic bytes — allow-list strictă separată de `sniff_registered_type`
 * (documents.ts), ca acceptarea WebP/HEIC la poze să NU lărgească și documentele înregistrate.
 * null = respins (fail-closed; extensia/Content-Type se pot minți).
 */
export function sniff_photo_type(bytes: Uint8Array): { ext: string; mime: string } | null {
	if (bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
		return { ext: "jpg", mime: "image/jpeg" }
	}
	if (
		bytes.length > 4 &&
		bytes[0] === 0x89 &&
		bytes[1] === 0x50 &&
		bytes[2] === 0x4e &&
		bytes[3] === 0x47
	) {
		return { ext: "png", mime: "image/png" }
	}
	// WebP: "RIFF" .... "WEBP"
	if (
		bytes.length > 12 &&
		bytes[0] === 0x52 &&
		bytes[1] === 0x49 &&
		bytes[2] === 0x46 &&
		bytes[3] === 0x46 &&
		bytes[8] === 0x57 &&
		bytes[9] === 0x45 &&
		bytes[10] === 0x42 &&
		bytes[11] === 0x50
	) {
		return { ext: "webp", mime: "image/webp" }
	}
	// HEIC/HEIF (iPhone): container ISO-BMFF — "ftyp" la offset 4, brandul la offset 8.
	if (
		bytes.length > 12 &&
		bytes[4] === 0x66 &&
		bytes[5] === 0x74 &&
		bytes[6] === 0x79 &&
		bytes[7] === 0x70
	) {
		const brand = String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!)
		if (["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"].includes(brand)) {
			return { ext: "heic", mime: "image/heic" }
		}
	}
	return null
}
